import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Clock, Users, Award, MapPin, Monitor, CheckCircle, Mail, ArrowRight, Star, Calendar } from 'lucide-react';
import { courses } from '../mock';
import { Certificate } from './Certificate';

export const Courses = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const handleEnrollClick = (course) => {
    const subject = `Enrollment Inquiry - ${course.title}`;
    const body = `Hello Phoenix WellControl Academy,

I am interested in enrolling for the following course:

Course: ${course.title}
Duration: ${course.duration}
Group Price: ${course.groupPriceRange}
Format: ${course.format}

Please provide me with more details about:
- Available dates
- Exact pricing
- Prerequisites (if any)
- Registration process

Looking forward to your response.

Best regards,`;
    
    window.location.href = `mailto:hello@phoenixwellcontrol.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section id="courses" className="py-20 bg-gradient-to-b from-orange-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header - Offset Design */}
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-8 lg:col-start-3">
            <div className="text-center space-y-6">
              <div className="inline-flex items-center space-x-2 bg-orange-100 px-6 py-3 rounded-full">
                <Star className="h-5 w-5 text-orange-600" />
                <span className="text-orange-700 font-semibold">Training Programs</span>
              </div>
              <h2 className="text-5xl font-black text-gray-900 leading-tight">
                Specialized Drilling Safety <br />
                <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                  Training
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Comprehensive oil and gas safety training programs designed specifically for non-drilling personnel. 
                Choose from our range of technical drilling safety courses, BOP training, and rig safety programs 
                to enhance operational safety across India.
              </p>
            </div>
          </div>
        </div>

        {/* Courses - Alternating Layout */}
        <div className="space-y-16">
          {courses.map((course, index) => {
            const isEven = index % 2 === 0;
            const cardColors = [
              'from-orange-500 to-red-500',
              'from-yellow-500 to-orange-500',
              'from-blue-500 to-indigo-500',
              'from-purple-500 to-pink-500',
              'from-green-500 to-teal-500',
              'from-red-500 to-pink-500'
            ];
            
            return (
              <div key={course.id} className={`grid lg:grid-cols-12 gap-8 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                {/* Course Info */}
                <div className={`lg:col-span-7 ${!isEven ? 'lg:col-start-6' : ''} space-y-6`}>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${cardColors[index]} flex items-center justify-center text-white font-bold text-sm`}>
                        {index + 1}
                      </div>
                      <Badge variant="secondary" className={`bg-gradient-to-r ${cardColors[index]} text-white border-0 px-3 py-1`}>
                        {course.duration}
                      </Badge>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-gray-900 leading-tight">
                      {course.title}
                    </h3>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {course.description}
                    </p>
                    
                    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-xl border border-orange-200">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          {course.groupPrice}
                        </div>
                        <div className="text-sm text-gray-600">
                          Group Training (1-15 candidates)
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Users className="h-5 w-5 text-orange-600" />
                        <span className="font-semibold text-gray-900">Target Audience</span>
                      </div>
                      <div className="space-y-1 pl-8">
                        {course.targetAudience.slice(0, 3).map((audience, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                            <span className="text-sm text-gray-600">{audience}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Award className="h-5 w-5 text-green-600" />
                        <span className="font-semibold text-gray-900">Certification</span>
                      </div>
                      <p className="text-sm text-gray-600 pl-8">{course.certificate}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 pt-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-full border-2 hover:scale-105 transition-transform">
                          View Details
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-2xl font-bold">{course.title}</DialogTitle>
                          <DialogDescription className="text-base leading-relaxed">
                            {course.description}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-8 mt-6">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <h4 className="font-bold text-gray-900 text-lg">Course Information</h4>
                              <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                  <Clock className="h-4 w-4 text-orange-600" />
                                  <span className="text-sm"><strong>Duration:</strong> {course.duration}</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <MapPin className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm"><strong>Format:</strong> {course.format}</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <Award className="h-4 w-4 text-green-600" />
                                  <span className="text-sm"><strong>Certificate:</strong> {course.certificate}</span>
                                </div>
                                <div className="bg-orange-50 p-3 rounded-lg">
                                  <div className="text-center">
                                    <div className="text-lg font-bold text-gray-900">
                                      {course.groupPrice}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      Group Training (1-15 candidates)
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <h4 className="font-bold text-gray-900 text-lg">Who Should Attend</h4>
                              <div className="space-y-2">
                                {course.targetAudience.map((audience, idx) => (
                                  <div key={idx} className="flex items-center space-x-2">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span className="text-sm text-gray-600">{audience}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg mb-4">Course Curriculum</h4>
                            <div className="grid gap-3">
                              {course.topics.map((topic, idx) => (
                                <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                                  <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${cardColors[index]} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5`}>
                                    {idx + 1}
                                  </div>
                                  <span className="text-sm text-gray-700 leading-relaxed">{topic}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-2xl border border-orange-200">
                            <h4 className="font-bold text-gray-900 mb-3">Ready to Get Started?</h4>
                            <p className="text-sm text-gray-700 mb-4">
                              Contact our training team for enrollment details, scheduling, and pricing information.
                            </p>
                            <Button 
                              onClick={() => handleEnrollClick(course)}
                              className={`bg-gradient-to-r ${cardColors[index]} hover:opacity-90 text-white rounded-full`}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Contact for Enrollment
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Button 
                      onClick={() => handleEnrollClick(course)}
                      className={`bg-gradient-to-r ${cardColors[index]} hover:opacity-90 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105`}
                    >
                      Enroll Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Course Visual */}
                <div className={`lg:col-span-5 ${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className="relative group">
                    <div className={`absolute -inset-4 bg-gradient-to-r ${cardColors[index]} rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                    <Card className="relative border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                      <div className={`h-4 bg-gradient-to-r ${cardColors[index]}`}></div>
                      <CardHeader className="bg-white p-8">
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <Badge variant="secondary" className={`bg-gradient-to-r ${cardColors[index]} text-white border-0 px-3 py-1`}>
                              {course.duration}
                            </Badge>
                            <div className="flex items-center space-x-2 text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <span className="text-sm font-medium">{course.duration}</span>
                            </div>
                          </div>
                          
                          <div className="text-center mb-4">
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-xl border border-blue-200">
                              <h4 className="text-sm font-bold text-gray-900 mb-2">Certificate Preview</h4>
                              <div 
                                className="bg-white rounded-lg p-1 shadow-sm mx-auto w-24 h-16 overflow-hidden cursor-pointer hover:shadow-md transition-shadow hover:scale-105 transform duration-200"
                                onClick={() => setSelectedCertificate(course)}
                              >
                                <div className="transform scale-[0.15] origin-top-left -ml-8 -mt-6">
                                  <Certificate course={course} />
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mt-2">
                                Click to view full certificate
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-4 bg-gray-50 rounded-xl">
                              <MapPin className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                              <div className="text-sm font-medium text-gray-900">Classroom</div>
                              <div className="text-xs text-gray-600">Gujarat Facility</div>
                            </div>
                            <div className="text-center p-4 bg-gray-50 rounded-xl">
                              <Monitor className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                              <div className="text-sm font-medium text-gray-900">Virtual</div>
                              <div className="text-xs text-gray-600">Online Training</div>
                            </div>
                          </div>
                          
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <Award className="h-5 w-5 text-green-600" />
                              <span className="font-semibold text-green-800">Certification Included</span>
                            </div>
                            <p className="text-sm text-green-700">{course.certificate}</p>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Training Delivery Options */}
        <div className="mt-20 bg-gradient-to-br from-slate-900 to-orange-900 rounded-3xl p-8 lg:p-12 text-white">
          <div className="text-center mb-10">
            <h3 className="text-4xl font-black mb-4">
              Flexible Oil & Gas Training <span className="text-orange-300">Delivery Options</span>
            </h3>
            <p className="text-xl text-orange-100">Choose from multiple training formats that work best for your schedule and location across India</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all group">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                  <MapPin className="h-10 w-10 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-white">Gujarat Drilling Safety Training Center</h4>
                <p className="text-orange-100 leading-relaxed">
                  State-of-the-art oil and gas training facilities in Gujarat, India with hands-on drilling simulations, 
                  well control equipment, and practical BOP training exercises for comprehensive learning experience.
                </p>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all group">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                  <Monitor className="h-10 w-10 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-white">Virtual Well Control Training</h4>
                <p className="text-orange-100 leading-relaxed">
                  Interactive online drilling safety courses with real-time instructor guidance, virtual drilling scenarios, 
                  and remote BOP systems training for oil and gas professionals across India and internationally.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Certificate Modal */}
        {selectedCertificate && (
          <Dialog open={!!selectedCertificate} onOpenChange={() => setSelectedCertificate(null)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-center">Sample Certificate</DialogTitle>
                <DialogDescription className="text-center text-lg">
                  {selectedCertificate.title}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-200">
                  <div className="bg-white rounded-lg p-4 shadow-lg">
                    <Certificate course={selectedCertificate} />
                  </div>
                  <div className="text-center mt-4">
                    <p className="text-gray-700 font-medium">
                      This is a sample certificate template. Upon successful completion of the training program, 
                      participants will receive an official certificate similar to this design.
                    </p>
                    <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200">
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                          <div className="font-bold text-gray-900">Certificate Validity</div>
                          <div className="text-gray-600">2 Years from completion</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-gray-900">Format Options</div>
                          <div className="text-gray-600">{selectedCertificate.format}</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-gray-900">Group Training</div>
                          <div className="text-gray-600">{selectedCertificate.groupPrice}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-center mt-6">
                  <Button 
                    onClick={() => {
                      setSelectedCertificate(null);
                      handleEnrollClick(selectedCertificate);
                    }}
                    className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white px-8 py-3 rounded-xl font-bold text-lg mr-4"
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Enroll for This Course
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setSelectedCertificate(null)}
                    className="px-8 py-3 rounded-xl font-bold text-lg"
                  >
                    Close Preview
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </section>
  );
};